import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatINR, GST_RATES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Download, ArrowLeft, Pencil, Save, X, Plus, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function InvoiceReviewPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [clients, setClients] = useState([]);
  const [editForm, setEditForm] = useState({ client_id: "", items: [], notes: "" });
  const [saving, setSaving] = useState(false);
  // Prior-period guard
  const [priorPeriodPrompt, setPriorPeriodPrompt] = useState(null); // { warning, invoice_number, invoice_date }
  const [pendingPayload, setPendingPayload] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get(`/invoices/${id}`), api.get("/clients")])
      .then(([invRes, cliRes]) => {
        setInvoice(invRes.data);
        setClients(cliRes.data);
      })
      .catch(() => toast.error("Invoice not found"))
      .finally(() => setLoading(false));
  }, [id]);

  const startEdit = () => {
    setEditForm({
      client_id: invoice.client_id,
      items: invoice.items.map((i) => ({
        description: i.description, hsn_sac: i.hsn_sac || "", quantity: i.quantity,
        rate: String(i.rate), gst_rate: String(i.gst_rate),
      })),
      notes: invoice.notes || "",
    });
    setEditing(true);
  };

  const updateEditItem = (idx, field, value) => {
    const updated = [...editForm.items];
    updated[idx] = { ...updated[idx], [field]: value };
    setEditForm({ ...editForm, items: updated });
  };

  const addEditItem = () => {
    const defaultGst = (user?.gst_status === "unregistered" || user?.gst_status === "registered_composition") ? "0" : "18";
    setEditForm({ ...editForm, items: [...editForm.items, { description: "", hsn_sac: "", quantity: 1, rate: "", gst_rate: defaultGst }] });
  };

  const removeEditItem = (idx) => {
    setEditForm({ ...editForm, items: editForm.items.filter((_, i) => i !== idx) });
  };

  const submitInvoiceUpdate = async (payload, withOverride = false) => {
    const url = withOverride ? `/invoices/${id}?prior_period_override=true` : `/invoices/${id}`;
    setSaving(true);
    try {
      const response = await api.put(url, payload);
      setInvoice(response.data);
      setEditing(false);
      setPriorPeriodPrompt(null);
      setPendingPayload(null);
      toast.success(withOverride ? "Invoice updated (prior-period override logged)" : "Invoice updated!");
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Backend returns 409 with a structured prior-period guard payload
      if (err.response?.status === 409 && detail?.code === "prior_period_edit_blocked") {
        setPriorPeriodPrompt(detail);
        setPendingPayload(payload);
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to update invoice");
      }
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    if (editForm.items.some((i) => !i.description || !i.rate)) {
      toast.error("Fill in all item details");
      return;
    }
    const payload = {
      client_id: editForm.client_id,
      items: editForm.items.map((i) => ({ ...i, quantity: Number(i.quantity), rate: Number(i.rate), gst_rate: Number(i.gst_rate) })),
      notes: editForm.notes,
    };
    await submitInvoiceUpdate(payload, false);
  };

  const confirmPriorPeriodEdit = () => {
    if (pendingPayload) submitInvoiceUpdate(pendingPayload, true);
  };

  const cancelPriorPeriodEdit = () => {
    setPriorPeriodPrompt(null);
    setPendingPayload(null);
  };

  const downloadPDF = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/invoices/${id}/pdf`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice_${invoice.invoice_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch {
      toast.error("Failed to generate PDF");
    }
  };

  const updateStatus = async (status) => {
    try {
      const response = await api.put(`/invoices/${id}/status`, { status });
      setInvoice(response.data);
      toast.success(`Invoice marked as ${status}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  if (loading) return <div className="p-8 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#4338ca] border-t-transparent" /></div>;
  if (!invoice) return <div className="p-8 text-center text-[#64748b]">Invoice not found.</div>;

  const hasCGST = invoice.cgst_amount > 0;
  const hasIGST = invoice.igst_amount > 0;
  const gstStatus = invoice.gst_status || user?.gst_status || "registered_regular";
  const isGST = gstStatus === "registered_regular";
  const isExport = invoice.is_export === true;
  const headerLabel = isExport ? "EXPORT INVOICE" : invoice.doc_type === "bill_of_supply" ? "BILL OF SUPPLY" : isGST ? "TAX INVOICE" : "COMMERCIAL INVOICE";

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto space-y-6" data-testid="invoice-review-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Button variant="ghost" onClick={() => navigate("/invoices")} data-testid="back-to-invoices">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <div className="flex items-center gap-2 flex-wrap">
          {!editing && (
            <>
              <Button variant="outline" size="sm" onClick={startEdit} data-testid="edit-invoice-btn" className="rounded-lg">
                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
              </Button>
              <Select value={invoice.status} onValueChange={updateStatus}>
                <SelectTrigger data-testid="invoice-status-select" className="w-28 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={downloadPDF} data-testid="download-pdf-btn" className="bg-[#4338ca] hover:bg-[#3730a3] rounded-xl shadow-sm h-9">
                <Download className="h-4 w-4 mr-2" /> PDF
              </Button>
            </>
          )}
          {editing && (
            <>
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)}><X className="h-3.5 w-3.5 mr-1" /> Cancel</Button>
              <Button size="sm" onClick={saveEdit} disabled={saving} data-testid="save-invoice-edit-btn" className="bg-[#4338ca] hover:bg-[#3730a3] rounded-lg">
                <Save className="h-3.5 w-3.5 mr-1.5" /> {saving ? "Saving..." : "Save Changes"}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Edit Mode */}
      {editing && (
        <Card className="bg-white border-[#4338ca]/20 shadow-md" data-testid="invoice-edit-form">
          <CardContent className="p-6 space-y-4">
            <div>
              <Label className="text-sm font-medium">Client</Label>
              <Select value={editForm.client_id} onValueChange={(v) => setEditForm({ ...editForm, client_id: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.client_id} value={c.client_id}>{c.name} ({c.state})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Line Items</Label>
              {editForm.items.map((item, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-end">
                  <div className="col-span-3">
                    <Input placeholder="Description" value={item.description} onChange={(e) => updateEditItem(idx, "description", e.target.value)} className="text-sm" />
                  </div>
                  <div className="col-span-2">
                    <Input placeholder="HSN/SAC" value={item.hsn_sac} onChange={(e) => updateEditItem(idx, "hsn_sac", e.target.value)} className="text-sm" />
                  </div>
                  <div className="col-span-1">
                    <Input type="number" value={item.quantity} onChange={(e) => updateEditItem(idx, "quantity", e.target.value)} className="text-sm" />
                  </div>
                  <div className="col-span-2">
                    <Input type="number" value={item.rate} onChange={(e) => updateEditItem(idx, "rate", e.target.value)} className="text-sm" />
                  </div>
                  {isGST && (
                    <div className="col-span-2">
                      <Select value={String(item.gst_rate)} onValueChange={(v) => updateEditItem(idx, "gst_rate", v)}>
                        <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>{GST_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className={isGST ? "col-span-2" : "col-span-4"}>
                    {editForm.items.length > 1 && (
                      <Button size="sm" variant="ghost" onClick={() => removeEditItem(idx)} className="text-red-500"><Trash2 className="h-3 w-3" /></Button>
                    )}
                  </div>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={addEditItem}><Plus className="h-3 w-3 mr-1" /> Add Item</Button>
            </div>
            <div>
              <Label className="text-sm font-medium">Notes</Label>
              <Input value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="mt-1.5" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview */}
      {!editing && (
        <Card className="bg-white shadow-lg border border-[#e2e8f0] rounded-xl" data-testid="invoice-preview">
          <CardContent className="p-8 md:p-12">
            <div className="flex justify-between items-start mb-10">
              <div>
                <h2 className="font-outfit text-2xl font-bold text-[#0f172a]">{user?.business_name}</h2>
                {user?.gstin && <p className="text-sm text-[#64748b] mt-1 font-mono">GSTIN: {user.gstin}</p>}
                <p className="text-sm text-[#64748b]">{user?.home_state}</p>
              </div>
              <div className="text-right">
                <h3 className="font-outfit text-xl font-bold text-[#4338ca]" data-testid="invoice-header-label">{headerLabel}</h3>
                <p className="font-mono text-sm text-[#64748b] mt-1">{invoice.invoice_number}</p>
                <p className="text-sm text-[#64748b]">{invoice.date}</p>
                {invoice.due_date && <p className="text-xs text-[#94a3b8]">Due: {invoice.due_date}</p>}
                <Badge className={`mt-2 ${invoice.status === "paid" ? "bg-[#10b981] text-white" : invoice.status === "sent" ? "bg-[#3b82f6] text-white" : "bg-[#f1f5f9] text-[#64748b]"}`}>
                  {invoice.status.toUpperCase()}
                </Badge>
              </div>
            </div>

            <div className="mb-8 p-4 bg-[#f8fafc] rounded-lg">
              <p className="text-xs text-[#64748b] font-medium uppercase tracking-wider mb-1">Bill To</p>
              <p className="font-semibold text-[#0f172a]">{invoice.client_name}</p>
              {invoice.client_gstin && <p className="text-sm text-[#64748b] font-mono">GSTIN: {invoice.client_gstin}</p>}
              <p className="text-sm text-[#64748b]">Place of Supply: {invoice.place_of_supply}</p>
            </div>

            {/* Export Details */}
            {isExport && (
              <div className="mb-6 p-4 bg-[#4338ca]/[0.03] rounded-lg border border-[#4338ca]/10" data-testid="export-details-section">
                <p className="text-xs text-[#4338ca] font-semibold uppercase tracking-wider mb-2">Export Details</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <span className="text-[#64748b] text-xs">Type</span>
                    <p className="font-medium text-[#0f172a]">{invoice.export_type === "lut" ? "Under LUT (Zero Tax)" : "With IGST Payment"}</p>
                  </div>
                  {invoice.lut_number && (
                    <div>
                      <span className="text-[#64748b] text-xs">LUT Number</span>
                      <p className="font-mono font-medium text-[#0f172a]">{invoice.lut_number}</p>
                    </div>
                  )}
                  {invoice.shipping_bill_number && (
                    <div>
                      <span className="text-[#64748b] text-xs">Shipping Bill</span>
                      <p className="font-mono font-medium text-[#0f172a]">{invoice.shipping_bill_number}</p>
                    </div>
                  )}
                  {invoice.port_code && (
                    <div>
                      <span className="text-[#64748b] text-xs">Port Code</span>
                      <p className="font-mono font-medium text-[#0f172a]">{invoice.port_code}</p>
                    </div>
                  )}
                  {invoice.currency && invoice.exchange_rate > 0 && (
                    <div>
                      <span className="text-[#64748b] text-xs">Currency</span>
                      <p className="font-mono font-medium text-[#0f172a]">{invoice.currency} (1 = Rs. {invoice.exchange_rate})</p>
                    </div>
                  )}
                </div>
                {invoice.export_declaration && (
                  <p className="mt-3 text-xs text-[#64748b] italic border-t border-[#4338ca]/10 pt-2">{invoice.export_declaration}</p>
                )}
              </div>
            )}

            <table className="w-full text-sm mb-8">
              <thead>
                <tr className="border-b-2 border-[#4338ca]">
                  <th className="text-left py-3 text-[#0f172a] font-semibold">#</th>
                  <th className="text-left py-3 text-[#0f172a] font-semibold">Description</th>
                  <th className="text-left py-3 text-[#0f172a] font-semibold">HSN/SAC</th>
                  <th className="text-right py-3 text-[#0f172a] font-semibold">Qty</th>
                  <th className="text-right py-3 text-[#0f172a] font-semibold">Rate</th>
                  {(isGST || isExport) && invoice.export_type !== "lut" && <th className="text-right py-3 text-[#0f172a] font-semibold">Tax Rate</th>}
                  {(isGST || isExport) && invoice.export_type !== "lut" && <th className="text-right py-3 text-[#0f172a] font-semibold">Tax Amt</th>}
                  <th className="text-right py-3 text-[#0f172a] font-semibold">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items?.map((item, idx) => {
                  const taxAmt = (item.cgst || 0) + (item.sgst || 0) + (item.igst || 0);
                  return (
                    <tr key={idx} className="border-b border-[#e2e8f0]">
                      <td className="py-3 text-[#64748b]">{idx + 1}</td>
                      <td className="py-3 text-[#0f172a]">{item.description}</td>
                      <td className="py-3 text-[#64748b] font-mono text-xs">{item.hsn_sac || "-"}</td>
                      <td className="py-3 text-right text-[#0f172a]">{item.quantity}</td>
                      <td className="py-3 text-right font-mono text-[#0f172a]">{formatINR(item.rate)}</td>
                      {(isGST || isExport) && invoice.export_type !== "lut" && <td className="py-3 text-right text-[#64748b]">{item.gst_rate}%</td>}
                      {(isGST || isExport) && invoice.export_type !== "lut" && <td className="py-3 text-right font-mono text-[#64748b]">{formatINR(taxAmt)}</td>}
                      <td className="py-3 text-right font-mono font-semibold text-[#0f172a]">{formatINR(item.amount)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="flex justify-end">
              <div className="w-80 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-[#64748b]">Subtotal</span>
                  <span className="font-mono font-medium text-[#0f172a]">{formatINR(invoice.subtotal)}</span>
                </div>
                {isGST && hasCGST && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#64748b]">CGST</span>
                      <span className="font-mono text-[#0f172a]">{formatINR(invoice.cgst_amount)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#64748b]">SGST</span>
                      <span className="font-mono text-[#0f172a]">{formatINR(invoice.sgst_amount)}</span>
                    </div>
                  </>
                )}
                {(isGST || isExport) && hasIGST && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[#64748b]">IGST{isExport ? " (Refundable)" : ""}</span>
                    <span className="font-mono text-[#0f172a]">{formatINR(invoice.igst_amount)}</span>
                  </div>
                )}
                {invoice.rounding_adjustment != null && invoice.rounding_adjustment !== 0 && (
                  <div className="flex justify-between text-xs" data-testid="rounding-adjustment-row">
                    <span className="text-[#94a3b8]">Rounding Adjustment</span>
                    <span className="font-mono text-[#94a3b8]">
                      {invoice.rounding_adjustment > 0 ? "+" : ""}{formatINR(invoice.rounding_adjustment)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-base font-bold pt-2 border-t-2 border-[#4338ca]">
                  <span className="text-[#0f172a]">Grand Total</span>
                  <span className="font-mono text-[#4338ca]" data-testid="grand-total-value">
                    {formatINR(invoice.grand_total || invoice.total)}
                  </span>
                </div>
                {/* Foreign Currency Equivalent */}
                {invoice.currency && invoice.exchange_rate > 0 && (
                  <div className="mt-2 pt-2 border-t border-dashed border-[#e2e8f0]" data-testid="foreign-currency-section">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#4338ca] font-medium">Equivalent ({invoice.currency})</span>
                      <span className="font-mono font-bold text-[#4338ca]" data-testid="foreign-currency-total">
                        {invoice.currency} {invoice.foreign_currency_total?.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs mt-0.5">
                      <span className="text-[#94a3b8]">Exchange Rate</span>
                      <span className="text-[#94a3b8] font-mono">1 {invoice.currency} = Rs. {invoice.exchange_rate?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {invoice.notes && (
              <div className="mt-8 pt-4 border-t border-[#e2e8f0]">
                <p className="text-xs text-[#64748b] font-medium uppercase mb-1">Notes</p>
                <p className="text-sm text-[#0f172a]">{invoice.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Prior-period GST edit confirmation */}
      <Dialog open={!!priorPeriodPrompt} onOpenChange={(o) => { if (!o) cancelPriorPeriodEdit(); }}>
        <DialogContent data-testid="prior-period-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#b45309]">
              <AlertTriangle className="h-5 w-5" /> Prior GST Return Period
            </DialogTitle>
            <DialogDescription className="text-[#475569] pt-2 whitespace-pre-line" data-testid="prior-period-warning-text">
              {priorPeriodPrompt?.message}
            </DialogDescription>
          </DialogHeader>
          {priorPeriodPrompt && (
            <div className="rounded-lg bg-[#fef3c7] border border-[#fde68a] p-3 text-xs text-[#92400e] space-y-1">
              <div className="font-mono"><span className="opacity-70">Invoice:</span> {priorPeriodPrompt.invoice_number}</div>
              <div className="font-mono"><span className="opacity-70">Date:</span> {priorPeriodPrompt.invoice_date}</div>
              <div className="opacity-80 mt-1">A revision history entry will be appended to this invoice if you proceed.</div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={cancelPriorPeriodEdit} data-testid="prior-period-cancel" disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={confirmPriorPeriodEdit}
              data-testid="prior-period-confirm"
              disabled={saving}
              className="bg-[#b45309] hover:bg-[#92400e] text-white"
            >
              {saving ? "Saving…" : "Proceed Anyway"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
